import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import {
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Award,
  Plus,
  Trash2,
  Save
} from 'lucide-react';

import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PageTitle from '../../components/page-title';
import { LoadingMessage } from '../../components/loading-states';

const schema = Yup.object().shape({
  nombre: Yup.string().required('El nombre del evento es obligatorio'),
  fecha: Yup.date()
    .min(new Date(), 'La fecha no puede ser anterior al día actual')
    .required('La fecha del evento es obligatoria'),
  horaInicio: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (ej. 14:00)')
    .required('La hora de inicio es obligatoria'),
  horaTermino: Yup.string()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (ej. 18:00)')
    .required('La hora de término es obligatoria'),
  lugar: Yup.string().required('El lugar/sede es obligatorio'),
  direccion: Yup.string().required('La dirección es obligatoria'),
  ciudad: Yup.string().required('La ciudad es obligatoria'),
  organizadorPrincipal: Yup.string().required('El organizador principal es obligatorio'),
  responsableNombre: Yup.string().required('El nombre del responsable es obligatorio'),
  responsableCargo: Yup.string().required('El cargo del responsable es obligatorio'),
  responsableCorreo: Yup.string().email('Correo inválido'),
  responsableTelefono: Yup.string(),
  tipoEvento: Yup.string().oneOf(['presencial', 'virtual', 'hibrido']).required('Selecciona el tipo de evento'),
  objetivo: Yup.string().required('El objetivo del evento es obligatorio'),
  publicoObjetivo: Yup.string().required('El público objetivo es obligatorio'),
  descripcionGeneral: Yup.string().required('La descripción general es obligatoria'),
  actividades: Yup.array().min(1, 'Selecciona al menos una actividad'),
  otraActividad: Yup.string(),
  cupoMaximo: Yup.number()
    .typeError('Debe ser un número')
    .positive('El cupo debe ser mayor que 0')
    .required('El cupo máximo es obligatorio'),
  requiereRegistro: Yup.boolean().required('Indica si requiere registro'),
  infoRecopilar: Yup.string().when('requiereRegistro', {
    is: true,
    then: (schema) => schema.required('Especifica qué información se recopilará')
  }),
  tipoCosto: Yup.string().oneOf(['gratuito', 'pago']).required('Indica si es gratuito o de pago'),
  precio: Yup.number().when('tipoCosto', {
    is: 'pago',
    then: (schema) => schema.required('El precio es obligatorio').positive('El precio debe ser mayor que 0')
  }),
  moneda: Yup.string().when('tipoCosto', {
    is: 'pago',
    then: (schema) => schema.oneOf(['MXN', 'USD']).required('Selecciona la moneda')
  }),
  elementosDestacados: Yup.string(),
  linkRegistro: Yup.string().url('Debe ser un enlace válido'),
  landingPage: Yup.string().url('Debe ser un enlace válido'),
  eventoFacebook: Yup.string().url('Debe ser un enlace válido'),
  enlaceVirtual: Yup.string().url('Debe ser un enlace válido (Zoom, Teams, etc.)'),
  creditos: Yup.number()
    .typeError('Debe ser un número')
    .min(0, 'Los créditos no pueden ser negativos')
});

interface Ponente {
  nombre: string;
  cargo: string;
  empresa: string;
  tema: string;
  horario: string;
}

interface Patrocinador {
  nombre: string;
  tipo: 'aliado' | 'patrocinador' | 'expositor';
  logo: string;
  link: string;
}

interface AgendaItem {
  horario: string;
  actividad: string;
  descripcion: string;
}

export default function CreateTaller() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [tipoCosto, setTipoCosto] = useState('');
  const [requiereRegistro, setRequiereRegistro] = useState(false);
  const [tipoEvento, setTipoEvento] = useState('');
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [ponentes, setPonentes] = useState<Ponente[]>([]);
  const [patrocinadores, setPatrocinadores] = useState<Patrocinador[]>([]);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [actividades, setActividades] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    // Validar acceso - permitir admin o admin de comité
    const tieneAcceso = user?.tipo?.includes('admin') || user?.esAdminComite === true;
    if (!user || !tieneAcceso) {
      Swal.fire({
        icon: 'error',
        title: 'Acceso denegado',
        text: 'No tienes permiso para acceder a esta sección.',
        confirmButtonColor: '#dc3545'
      });
      navigate('/dashboard');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  const actividadesOpciones = [
    'Conferencias',
    'Talleres',
    'Paneles',
    'Networking',
    'Comida',
    'Exposición de desarrollos',
    'Presentación de marca'
  ];

  const handleActividadChange = (actividad: string) => {
    const newActividades = actividades.includes(actividad)
      ? actividades.filter(a => a !== actividad)
      : [...actividades, actividad];
    setActividades(newActividades);
    setValue('actividades', newActividades);
  };

  const agregarPonente = () => {
    setPonentes([...ponentes, {
      nombre: '',
      cargo: '',
      empresa: '',
      tema: '',
      horario: ''
    }]);
  };

  const actualizarPonente = (index: number, campo: keyof Ponente, valor: string) => {
    const nuevosPonentes = [...ponentes];
    nuevosPonentes[index] = { ...nuevosPonentes[index], [campo]: valor };
    setPonentes(nuevosPonentes);
  };

  const eliminarPonente = (index: number) => {
    setPonentes(ponentes.filter((_, i) => i !== index));
  };

  const agregarPatrocinador = () => {
    setPatrocinadores([...patrocinadores, {
      nombre: '',
      tipo: 'patrocinador',
      logo: '',
      link: ''
    }]);
  };

  const actualizarPatrocinador = (index: number, campo: keyof Patrocinador, valor: string) => {
    const nuevosPatrocinadores = [...patrocinadores];
    nuevosPatrocinadores[index] = { ...nuevosPatrocinadores[index], [campo]: valor };
    setPatrocinadores(nuevosPatrocinadores);
  };

  const eliminarPatrocinador = (index: number) => {
    setPatrocinadores(patrocinadores.filter((_, i) => i !== index));
  };

  const agregarAgendaItem = () => {
    setAgenda([...agenda, {
      horario: '',
      actividad: '',
      descripcion: ''
    }]);
  };

  const actualizarAgendaItem = (index: number, campo: keyof AgendaItem, valor: string) => {
    const nuevaAgenda = [...agenda];
    nuevaAgenda[index] = { ...nuevaAgenda[index], [campo]: valor };
    setAgenda(nuevaAgenda);
  };

  const eliminarAgendaItem = (index: number) => {
    setAgenda(agenda.filter((_, i) => i !== index));
  };

  const generarEventoId = (nombre: string, fecha: Date): string => {
    const letras = nombre.replace(/[^a-zA-Z]/g, '').toLowerCase().slice(0, 3) || 'evt';
    const fechaObj = new Date(fecha);
    const dia = String(fechaObj.getDate()).padStart(2, '0');
    const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const anio = String(fechaObj.getFullYear()).slice(-2);
    return `${letras}${dia}${mes}${anio}`;
  };

  const onSubmit = async (data: any) => {
    setGuardando(true);
    try {
      let bannerUrl = '';
      if (bannerFile) {
        const formData = new FormData();
        formData.append('nombreTaller', data.nombre);
        formData.append('fecha', data.fecha);
        formData.append('banner', bannerFile);
        const res = await fetch(process.env.REACT_APP_UPLOAD_BANNER_URL || 'https://ampirivieranayarit.com/modulob/backend/upload-banner.php', {
          method: 'POST',
          body: formData
        });

        const json = await res.json();
        bannerUrl = json.url;
      }

      const evento = {
        taller_id: generarEventoId(data.nombre, data.fecha),

        // Información básica
        nombre: data.nombre,
        fecha: new Date(data.fecha),
        horaInicio: data.horaInicio,
        horaTermino: data.horaTermino,

        // Ubicación
        lugar: data.lugar,
        direccion: data.direccion,
        ciudad: data.ciudad,

        // Organización
        organizadorPrincipal: data.organizadorPrincipal,
        responsable: {
          nombre: data.responsableNombre,
          cargo: data.responsableCargo,
          correo: data.responsableCorreo || '',
          telefono: data.responsableTelefono || ''
        },

        // Detalles del evento
        tipoEvento: data.tipoEvento,
        enlaceVirtual: (data.tipoEvento === 'virtual' || data.tipoEvento === 'hibrido') ? (data.enlaceVirtual || '') : '',
        objetivo: data.objetivo,
        publicoObjetivo: data.publicoObjetivo,
        descripcionGeneral: data.descripcionGeneral,

        // Actividades
        actividades: actividades,
        otraActividad: data.otraActividad || '',

        // Ponentes
        ponentes: ponentes.filter(p => p.nombre),

        // Agenda
        agenda: agenda.filter(a => a.horario && a.actividad),

        // Capacidad
        cupoMaximo: parseInt(data.cupoMaximo),

        // Registro
        requiereRegistro: data.requiereRegistro,
        infoRecopilar: data.requiereRegistro ? data.infoRecopilar : '',

        // Costo
        tipoCosto: data.tipoCosto,
        precio: data.tipoCosto === 'pago' ? parseFloat(data.precio) : 0,
        moneda: data.tipoCosto === 'pago' ? data.moneda : '',

        // Patrocinadores
        patrocinadores: patrocinadores.filter(p => p.nombre),

        // Comunicación
        elementosDestacados: data.elementosDestacados || '',

        // Enlaces
        enlaces: {
          registro: data.linkRegistro || '',
          landingPage: data.landingPage || '',
          facebook: data.eventoFacebook || ''
        },

        // Créditos AMPI
        creditos: parseInt(data.creditos) || 0,

        // Imagen
        bannerUrl,

        // Metadatos
        fechaCreacion: new Date(),
        activo: true
      };

      const docRef = await addDoc(collection(db, 'taller'), evento);

      await Swal.fire({
        icon: 'success',
        title: '¡Evento Creado!',
        html: `
          <div class="text-start">
            <p><strong>Nombre:</strong> ${data.nombre}</p>
            <p><strong>Fecha:</strong> ${new Date(data.fecha).toLocaleDateString('es-MX')}</p>
            <p><strong>ID del evento:</strong> ${evento.taller_id}</p>
            <p class="text-success mb-0">✅ El evento fue creado exitosamente</p>
          </div>
        `,
        confirmButtonColor: '#28a745',
        confirmButtonText: 'Ver Lista de Talleres'
      });

      // Resetear formulario
      reset();
      setTipoCosto('');
      setRequiereRegistro(false);
      setTipoEvento('');
      setBannerFile(null);
      setPonentes([]);
      setPatrocinadores([]);
      setAgenda([]);
      setActividades([]);

      // Redirigir a la lista de talleres o dashboard
      navigate('/admin-dashboard');
    } catch (error: any) {
      console.error('Error al crear evento:', error.message);
      Swal.fire({
        icon: 'error',
        title: 'Error al Crear Evento',
        text: error.message || 'Ocurrió un error al crear el evento. Inténtalo nuevamente.',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Entendido'
      });
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Validando permisos...</p>
      </div>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <PageTitle
        title="Crear Evento / Taller"
        subtitle="Registra un nuevo evento siguiendo la ficha técnica de AMPI"
      />

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Crear Evento' }
            ]}
          />

          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <div className="d-flex align-items-center justify-content-center mb-4">
                  <Calendar size={32} className="text-primary me-3" />
                  <h2 className="mb-0 fw-bold" style={{ color: '#0c2339' }}>Ficha Técnica de Evento</h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="taller-form-modern">
                  {/* Sección 1: Información Básica */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Información Básica</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Nombre del Evento *</label>
                          <input {...register('nombre')} className="form-control"
                            placeholder="Ej. Summit Turístico Internacional" />
                          {errors.nombre && <p className="text-danger small">{errors.nombre.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Fecha del Evento *</label>
                          <input type="date" {...register('fecha')} className="form-control" />
                          {errors.fecha && <p className="text-danger small">{errors.fecha.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Inicio *</label>
                          <input {...register('horaInicio')} className="form-control"
                            placeholder="Ej. 09:00" />
                          {errors.horaInicio && <p className="text-danger small">{errors.horaInicio.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Hora de Término *</label>
                          <input {...register('horaTermino')} className="form-control"
                            placeholder="Ej. 18:00" />
                          {errors.horaTermino && <p className="text-danger small">{errors.horaTermino.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <div className="photo-upload">
                            <label>Banner del Evento (opcional)</label>
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                            />
                            <small className="text-muted">Imagen promocional del evento</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Ubicación */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Ubicación</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Lugar/Sede *</label>
                          <input {...register('lugar')} className="form-control"
                            placeholder="Nombre del recinto" />
                          {errors.lugar && <p className="text-danger small">{errors.lugar.message}</p>}
                        </div>

                        <div className="col-lg-8 mb-3">
                          <label>Dirección *</label>
                          <input {...register('direccion')} className="form-control"
                            placeholder="Calle, número, colonia" />
                          {errors.direccion && <p className="text-danger small">{errors.direccion.message}</p>}
                        </div>

                        <div className="col-lg-4 mb-3">
                          <label>Ciudad *</label>
                          <input {...register('ciudad')} className="form-control"
                            placeholder="Ej. Puerto Vallarta" />
                          {errors.ciudad && <p className="text-danger small">{errors.ciudad.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 3: Organización */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">3. Organización</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Organizador Principal *</label>
                          <input {...register('organizadorPrincipal')} className="form-control"
                            placeholder="Nombre del comité, sección o persona encargada" />
                          {errors.organizadorPrincipal && <p className="text-danger small">{errors.organizadorPrincipal.message}</p>}
                        </div>

                        <div className="col-12"><h6>Responsable del Evento / Contacto Directo</h6></div>

                        <div className="col-lg-6 mb-3">
                          <label>Nombre *</label>
                          <input {...register('responsableNombre')} className="form-control"
                            placeholder="Nombre del responsable" />
                          {errors.responsableNombre && <p className="text-danger small">{errors.responsableNombre.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Cargo *</label>
                          <input {...register('responsableCargo')} className="form-control"
                            placeholder="Director, Coordinador, etc." />
                          {errors.responsableCargo && <p className="text-danger small">{errors.responsableCargo.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Correo (opcional)</label>
                          <input type="email" {...register('responsableCorreo')} className="form-control"
                            placeholder="correo@ejemplo.com" />
                          {errors.responsableCorreo && <p className="text-danger small">{errors.responsableCorreo.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Teléfono (opcional)</label>
                          <input {...register('responsableTelefono')} className="form-control"
                            placeholder="+52 322 123 4567" />
                          {errors.responsableTelefono && <p className="text-danger small">{errors.responsableTelefono.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 4: Detalles del Evento */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">4. Detalles del Evento</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-6 mb-3">
                          <label>Tipo de Evento *</label>
                          <select {...register('tipoEvento')} className="form-control"
                            onChange={(e) => setTipoEvento(e.target.value)}>
                            <option value="">Selecciona</option>
                            <option value="presencial">Presencial</option>
                            <option value="virtual">Virtual</option>
                            <option value="hibrido">Híbrido</option>
                          </select>
                          {errors.tipoEvento && <p className="text-danger small">{errors.tipoEvento.message}</p>}
                        </div>

                        {/* Campo de enlace virtual - solo visible para eventos virtuales o híbridos */}
                        {(tipoEvento === 'virtual' || tipoEvento === 'hibrido') && (
                          <div className="col-lg-6 mb-3">
                            <label>
                              Enlace de la Sesión Virtual {tipoEvento === 'virtual' ? '*' : '(opcional)'}
                            </label>
                            <input
                              {...register('enlaceVirtual')}
                              className="form-control"
                              placeholder="https://zoom.us/j/... o https://teams.microsoft.com/..."
                            />
                            <small className="text-muted">
                              Link de Zoom, Teams, Google Meet u otra plataforma
                            </small>
                            {errors.enlaceVirtual && <p className="text-danger small">{errors.enlaceVirtual.message}</p>}
                          </div>
                        )}

                        <div className="col-lg-6 mb-3">
                          <label>Cupo Máximo *</label>
                          <input type="number" {...register('cupoMaximo')} className="form-control"
                            placeholder="Número de asistentes" />
                          {errors.cupoMaximo && <p className="text-danger small">{errors.cupoMaximo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Objetivo del Evento *</label>
                          <textarea {...register('objetivo')} className="form-control" rows={3}
                            placeholder="¿Cuál es el propósito principal? ¿Qué se quiere lograr?" />
                          {errors.objetivo && <p className="text-danger small">{errors.objetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Público Objetivo *</label>
                          <input {...register('publicoObjetivo')} className="form-control"
                            placeholder="Ej. Agentes inmobiliarios, inversionistas, desarrolladores" />
                          {errors.publicoObjetivo && <p className="text-danger small">{errors.publicoObjetivo.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Descripción General *</label>
                          <textarea {...register('descripcionGeneral')} className="form-control" rows={4}
                            placeholder="Breve explicación de la dinámica general del evento" />
                          {errors.descripcionGeneral && <p className="text-danger small">{errors.descripcionGeneral.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 5: Actividades */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">5. Actividades Incluidas</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-12 mb-3">
                          <label>Selecciona las actividades que incluirá el evento *</label>
                          <div className="row">
                            {actividadesOpciones.map((actividad) => (
                              <div key={actividad} className="col-md-6 mb-2">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={actividad}
                                    checked={actividades.includes(actividad)}
                                    onChange={() => handleActividadChange(actividad)}
                                  />
                                  <label className="form-check-label" htmlFor={actividad}>
                                    {actividad}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                          {errors.actividades && <p className="text-danger small">{errors.actividades.message}</p>}
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Otra actividad (especifica)</label>
                          <input {...register('otraActividad')} className="form-control"
                            placeholder="Si hay alguna actividad no listada" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 6: Ponentes */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">6. Ponentes Confirmados</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarPonente}>
                        <Plus size={16} className="me-1" /> Agregar Ponente
                      </button>
                    </div>
                    <div className="card-body">
                      {ponentes.length === 0 ? (
                        <p className="text-muted">No hay ponentes agregados. Haz clic en "Agregar Ponente".</p>
                      ) : (
                        ponentes.map((ponente, index) => (
                          <div key={index} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-6 mb-3">
                                <label>Nombre completo</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Dr. Juan Pérez"
                                  value={ponente.nombre}
                                  onChange={(e) => actualizarPonente(index, 'nombre', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Cargo</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Director de Marketing"
                                  value={ponente.cargo}
                                  onChange={(e) => actualizarPonente(index, 'cargo', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Empresa</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. AMPI Riviera Nayarit"
                                  value={ponente.empresa}
                                  onChange={(e) => actualizarPonente(index, 'empresa', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-6 mb-3">
                                <label>Horario</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. 10:00 - 11:00"
                                  value={ponente.horario}
                                  onChange={(e) => actualizarPonente(index, 'horario', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-12 mb-3">
                                <label>Tema o título de la ponencia</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Estrategias de marketing digital para inmobiliarias"
                                  value={ponente.tema}
                                  onChange={(e) => actualizarPonente(index, 'tema', e.target.value)}
                                />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarPonente(index)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Sección 7: Agenda */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">7. Agenda Tentativa</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarAgendaItem}>
                        <Plus size={16} className="me-1" /> Agregar Actividad
                      </button>
                    </div>
                    <div className="card-body">
                      {agenda.length === 0 ? (
                        <p className="text-muted">No hay actividades en la agenda. Haz clic en "Agregar Actividad".</p>
                      ) : (
                        agenda.map((item, index) => (
                          <div key={index} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-3 mb-3">
                                <label>Horario</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. 09:00 - 10:00"
                                  value={item.horario}
                                  onChange={(e) => actualizarAgendaItem(index, 'horario', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-4 mb-3">
                                <label>Actividad</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Registro de asistentes"
                                  value={item.actividad}
                                  onChange={(e) => actualizarAgendaItem(index, 'actividad', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-5 mb-3">
                                <label>Descripción</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Recepción y entrega de gafetes"
                                  value={item.descripcion}
                                  onChange={(e) => actualizarAgendaItem(index, 'descripcion', e.target.value)}
                                />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarAgendaItem(index)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Sección 8: Registro y Costo */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">8. Registro y Costo</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-6 mb-3">
                          <label>¿El evento requiere registro previo? *</label>
                          <select {...register('requiereRegistro')} className="form-control"
                            onChange={(e) => setRequiereRegistro(e.target.value === 'true')}>
                            <option value="">Selecciona</option>
                            <option value="true">Sí</option>
                            <option value="false">No</option>
                          </select>
                          {errors.requiereRegistro && <p className="text-danger small">{errors.requiereRegistro.message}</p>}
                        </div>

                        {requiereRegistro && (
                          <div className="col-lg-6 mb-3">
                            <label>¿Qué información se recopilará? *</label>
                            <input {...register('infoRecopilar')} className="form-control"
                              placeholder="Ej. Nombre, correo, teléfono, empresa" />
                            {errors.infoRecopilar && <p className="text-danger small">{errors.infoRecopilar.message}</p>}
                          </div>
                        )}

                        <div className="col-lg-6 mb-3">
                          <label>¿Es gratuito o con costo? *</label>
                          <select {...register('tipoCosto')} className="form-control"
                            onChange={(e) => setTipoCosto(e.target.value)}>
                            <option value="">Selecciona</option>
                            <option value="gratuito">Gratuito</option>
                            <option value="pago">Con costo</option>
                          </select>
                          {errors.tipoCosto && <p className="text-danger small">{errors.tipoCosto.message}</p>}
                        </div>

                        {tipoCosto === 'pago' && (
                          <>
                            <div className="col-lg-3 mb-3">
                              <label>Precio *</label>
                              <input type="number" {...register('precio')} className="form-control"
                                placeholder="1500" />
                              {errors.precio && <p className="text-danger small">{errors.precio.message}</p>}
                            </div>
                            <div className="col-lg-3 mb-3">
                              <label>Moneda *</label>
                              <select {...register('moneda')} className="form-control">
                                <option value="">Selecciona</option>
                                <option value="MXN">MXN</option>
                                <option value="USD">USD</option>
                              </select>
                              {errors.moneda && <p className="text-danger small">{errors.moneda.message}</p>}
                            </div>
                          </>
                        )}

                        <div className="col-lg-6 mb-3">
                          <label>Créditos AMPI (0 si no aplica)</label>
                          <input type="number" {...register('creditos')} className="form-control"
                            defaultValue="0" />
                          {errors.creditos && <p className="text-danger small">{errors.creditos.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 9: Patrocinadores */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">9. Aliados, Patrocinadores y Expositores</h5>
                      <button type="button" className="btn btn-sm btn-light" onClick={agregarPatrocinador}>
                        <Plus size={16} className="me-1" /> Agregar
                      </button>
                    </div>
                    <div className="card-body">
                      {patrocinadores.length === 0 ? (
                        <p className="text-muted">No hay patrocinadores agregados.</p>
                      ) : (
                        patrocinadores.map((patrocinador, index) => (
                          <div key={index} className="border rounded p-3 mb-3">
                            <div className="row">
                              <div className="col-lg-4 mb-3">
                                <label>Nombre</label>
                                <input
                                  className="form-control"
                                  placeholder="Ej. Hotel Grand Velas"
                                  value={patrocinador.nombre}
                                  onChange={(e) => actualizarPatrocinador(index, 'nombre', e.target.value)}
                                />
                              </div>
                              <div className="col-lg-3 mb-3">
                                <label>Tipo</label>
                                <select
                                  className="form-control"
                                  value={patrocinador.tipo}
                                  onChange={(e) => actualizarPatrocinador(index, 'tipo', e.target.value)}
                                >
                                  <option value="aliado">Aliado</option>
                                  <option value="patrocinador">Patrocinador</option>
                                  <option value="expositor">Expositor</option>
                                </select>
                              </div>
                              <div className="col-lg-5 mb-3">
                                <label>Link del sitio web</label>
                                <input
                                  className="form-control"
                                  placeholder="https://www.ejemplo.com"
                                  value={patrocinador.link}
                                  onChange={(e) => actualizarPatrocinador(index, 'link', e.target.value)}
                                />
                              </div>
                              <div className="col-12">
                                <button type="button" className="btn btn-sm btn-danger"
                                  onClick={() => eliminarPatrocinador(index)}>
                                  <Trash2 size={16} className="me-1" /> Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Sección 10: Comunicación y Enlaces */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">10. Comunicación y Enlaces</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12 mb-3">
                          <label>Elementos clave a destacar en la comunicación</label>
                          <textarea {...register('elementosDestacados')} className="form-control" rows={3}
                            placeholder="Ej. Conferencistas destacados, ubicaciones exclusivas, beneficios para asistentes" />
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label>Link de registro</label>
                          <input {...register('linkRegistro')} className="form-control"
                            placeholder="https://..." />
                          {errors.linkRegistro && <p className="text-danger small">{errors.linkRegistro.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Landing page</label>
                          <input {...register('landingPage')} className="form-control"
                            placeholder="https://..." />
                          {errors.landingPage && <p className="text-danger small">{errors.landingPage.message}</p>}
                        </div>

                        <div className="col-lg-6 mb-3">
                          <label>Evento en Facebook</label>
                          <input {...register('eventoFacebook')} className="form-control"
                            placeholder="https://facebook.com/events/..." />
                          {errors.eventoFacebook && <p className="text-danger small">{errors.eventoFacebook.message}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mt-5 text-center">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-5"
                      disabled={guardando}
                    >
                      {guardando ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Guardando Evento...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="me-2" />
                          Crear Evento
                        </>
                      )}
                    </button>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}